import { useEffect, useRef } from "react";
import Hls from "hls.js";
import { youtubeEmbedUrl } from "@/lib/api";

// Supports: youtube (iframe embed URL), hls (.m3u8), mp4 (direct video URL)
export default function LivePlayer({ url, streamType, titleTe, titleEn }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  useEffect(() => {
    if (!videoRef.current || !url) return;
    if (streamType !== "hls" && streamType !== "mp4") return;

    const video = videoRef.current;
    video.removeAttribute("src");
    video.load();
    let hls = null;

    if (streamType === "hls") {
      if (Hls.isSupported()) {
        hls = new Hls();
        hlsRef.current = hls;
        hls.loadSource(url);
        hls.attachMedia(video);
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = url;
      } else {
        video.setAttribute("data-stream-error", "unsupported");
      }
    } else if (streamType === "mp4") {
      video.src = url;
    }

    return () => {
      hls?.destroy();
      if (hlsRef.current === hls) hlsRef.current = null;
      video.pause();
      video.removeAttribute("src");
      video.load();
    };
  }, [url, streamType]);

  if (!url) {
    return <div className="aspect-video bg-black flex items-center justify-center text-white text-sm">No live stream configured</div>;
  }

  if (streamType === "youtube") {
    const embedUrl = youtubeEmbedUrl(url);
    if (!embedUrl) {
      return <div className="aspect-video bg-black flex items-center justify-center text-white text-sm">Invalid YouTube stream URL</div>;
    }
    return (
      <iframe
        className="w-full aspect-video"
        src={embedUrl}
        title={titleEn || "Live TV"}
        frameBorder="0"
        allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        data-testid="live-tv-iframe"
      />
    );
  }

  return (
    <video
      ref={videoRef}
      controls
      autoPlay
      muted
      playsInline
      className="w-full aspect-video bg-black"
      data-testid="live-tv-video"
    />
  );
}
